import { useCallback } from 'react';
import debounce from 'lodash/debounce';

import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import Switch from 'approot/shared/switch/switch';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useUpdateComponent } from 'approot/activity-builder/activity-builder.hooks';
import {
  SettingsProps,
  EquationResponseProperties,
  OPERATION_FIELD,
  LAYOUT_FIELD,
  MORE_INFORMATION_FIELD,
} from './equation-response.constants';
import {
  CheckableSingleGroup,
  CheckableSinglePill,
} from 'approot/shared/styles/buttons/buttons';
import {
  COMPONENT_LAYOUT_HORIZONTAL,
  COMPONENT_LAYOUT_VERTICAL,
  NUMBER_OPERATION_MINUS,
  NUMBER_OPERATION_MULTIPLY,
  NUMBER_OPERATION_PLUS,
} from 'global/constants';
import {
  ActivityBuilderDrawerWrapper,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { InputDescriptionStyled } from 'approot/shared/styles/inputs.style';
import { ComponentResponseTextArea } from '../shared/component-parts.style';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';

const EquationResponseSettings = ({
  id,
  sectionId,
  contentId,
  properties,
  type,
  sort,
  subActivityProperties,
  onCloseChildDrawer,
}: SettingsProps) => {
  const { closeDrawer } = useActivityBuilderContext();
  const { showError } = useSnackbar();

  const { updateComponent } = useUpdateComponent({
    onError: () =>
      showError('There was an error updating this equation response'),
  });

  const handlePropertyChange = useCallback(
    (newProperties: Partial<EquationResponseProperties>) => {
      const { __typename, layout, ...restProperties } = properties;
      const { layout: newLayoutProperties } = newProperties;
      const updatedProperties = {
        ...restProperties,
        ...newProperties,
        layout: newLayoutProperties
          ? removeTypenameFromObject(newLayoutProperties)
          : layout
            ? removeTypenameFromObject(layout)
            : null,
      };
      updateComponent({
        sectionId,
        componentId: id,
        componentData: {
          type,
          properties: updatedProperties,
          sort,
        },
      });
    },
    [id, sectionId, type, sort, properties, updateComponent]
  );

  const debounceMoreInformationChange = debounce(handlePropertyChange, 300);

  const isChildDrawer = !!onCloseChildDrawer;

  return (
    <ActivityBuilderDrawerWrapper
      captureKeypresses={!isChildDrawer}
      onCloseByEscape={isChildDrawer ? onCloseChildDrawer : closeDrawer}
    >
      <ActivityBuilderDrawerHeader
        title="Equation response"
        isBack={isChildDrawer}
        onClose={isChildDrawer ? onCloseChildDrawer : closeDrawer}
      />
      <ActivityBuilderDrawerContent>
        <SettingsGroup>
          <SettingsLabel htmlFor={OPERATION_FIELD}>Operation</SettingsLabel>
          <CheckableSingleGroup>
            <CheckableSinglePill
              $isRound
              aria-label="Set the response operator to add"
              aria-checked={properties.operation === NUMBER_OPERATION_PLUS}
              $isSelected={properties.operation === NUMBER_OPERATION_PLUS}
              onClick={() =>
                handlePropertyChange({
                  operation: NUMBER_OPERATION_PLUS,
                })
              }
            >
              +
            </CheckableSinglePill>
            <CheckableSinglePill
              $isRound
              aria-label="Set the response operator to subtract"
              aria-checked={properties.operation === NUMBER_OPERATION_MINUS}
              $isSelected={properties.operation === NUMBER_OPERATION_MINUS}
              onClick={() =>
                handlePropertyChange({
                  operation: NUMBER_OPERATION_MINUS,
                })
              }
            >
              -
            </CheckableSinglePill>
            <CheckableSinglePill
              $isRound
              aria-label="Set the response operator to multiply"
              aria-checked={properties.operation === NUMBER_OPERATION_MULTIPLY}
              $isSelected={properties.operation === NUMBER_OPERATION_MULTIPLY}
              onClick={() =>
                handlePropertyChange({
                  operation: NUMBER_OPERATION_MULTIPLY,
                })
              }
            >
              x
            </CheckableSinglePill>
          </CheckableSingleGroup>
        </SettingsGroup>

        <SettingsGroup>
          <SettingsLabel htmlFor={LAYOUT_FIELD}>Layout</SettingsLabel>
          <CheckableSingleGroup>
            <CheckableSinglePill
              aria-label="Set the response layout to vertical"
              aria-checked={
                properties.layout?.orientation === COMPONENT_LAYOUT_VERTICAL
              }
              $isSelected={
                properties.layout?.orientation === COMPONENT_LAYOUT_VERTICAL
              }
              onClick={() =>
                handlePropertyChange({
                  layout: {
                    orientation: COMPONENT_LAYOUT_VERTICAL,
                    __typename: 'ComponentLayout',
                  },
                })
              }
            >
              Vertical
            </CheckableSinglePill>

            <CheckableSinglePill
              aria-label="Set the response layout to horizontal"
              aria-checked={
                properties.layout?.orientation === COMPONENT_LAYOUT_HORIZONTAL
              }
              $isSelected={
                properties.layout?.orientation === COMPONENT_LAYOUT_HORIZONTAL
              }
              onClick={() =>
                handlePropertyChange({
                  layout: {
                    orientation: COMPONENT_LAYOUT_HORIZONTAL,
                    __typename: 'ComponentLayout',
                  },
                })
              }
            >
              Horizontal
            </CheckableSinglePill>
          </CheckableSingleGroup>
        </SettingsGroup>

        <SettingsGroup>
          <SettingsLabel>Reveal Correct Answer</SettingsLabel>
          <Switch
            state={properties.revealAnswer}
            ariaLabel="Reveal the correct answer when checking the response"
            onClick={() =>
              handlePropertyChange({ revealAnswer: !properties.revealAnswer })
            }
          />
        </SettingsGroup>

        <SettingsGroup>
          <SettingsLabel htmlFor={MORE_INFORMATION_FIELD}>
            More Information
          </SettingsLabel>
          <ComponentResponseTextArea
            id={MORE_INFORMATION_FIELD}
            placeholder="Type here..."
            initialValue={properties.moreInformation || ''}
            onChange={event =>
              debounceMoreInformationChange({
                moreInformation: event.target.value,
              })
            }
            data-selector="txt-more-information"
          />
          <InputDescriptionStyled>
            Use this to teach your students further, explaining the correct
            answer. It will appear when checking the answers.
          </InputDescriptionStyled>
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default EquationResponseSettings;
